<template>
    <div class="home">
		<el-container style="height:100vh;width:100%;overflow:hidden;" gutter="12">
			<TheMap :tagName="mapTag"/>
			<SpotList :locale="locale" ref="spotList" :initialLanguage="locale" :onLanguageChanged="onLanguageChanged" :spots="spots" :spotSelectedHandler="spotSelectedHandler" :searchBoxName="searchBoxTag" :searchQuery="searchQuery" :selectInput="selectInput"/>
			<ProfilesList :spot="selectedSpot" ref="profileList" :onPrintCall="onPrintCall"/>
			<UploadFile :fullscreen="fullscreen" :done="jobDone" :spot="selectedSpot" ref="selectFileDialog" :upload="uploadFilesConfirmation"/>			
		</el-container>
    </div>
</template>

<script>
// @ is an alias to /src
import TheMap   from '@/components/TheMap.vue';
import Helpers  from '@/utils/Helpers';
import Logger   from '@/utils/Logger';
import Ajax     from '@/utils/Ajax';
import SpotList from '@/components/SpotList.vue';
import ProfilesList from '@/components/ProfilesList.vue';
import UploadFile from '@/components/UploadFile.vue';
import Settings from '@/Settings.js';
import { PlacesFilter, PlacesModes } from '@/utils/PlacesFilter.js';
import SizeListener from '@/utils/SizeListener.js';

const DEFAULT_INITIAL_COORDS = {
    latitude:  -34.397,
    longitude: 150.644
};
const MAP_TAG        = "the-map";
const SEARCH_BOX_TAG = "the-search";
const MAP_ZOOM = 17;
const GOOGLE_MAPS_SCRIPT_URL  = `https://maps.googleapis.com/maps/api/js?key=${Settings.GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
const GOOGLE_MARKER_CLUSTERER = "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/markerclusterer.js";
const INITIAL_SEARCH_RADIUS = 5000; //meters

const MAP_UI_SETTINGS_SM = {
	zoomControl: true,
	mapTypeControl: false,
	scaleControl: true,
	streetViewControl: true,
	rotateControl: true,
	fullscreenControl: false
};

const MAP_UI_SETTINGS_XS = {
	zoomControl: false,
	mapTypeControl: false,
	scaleControl: false,
	streetViewControl: true,
	rotateControl: true,
	fullscreenControl: false
};

export default {
    name: 'home',
    data: () => ({
        mapTag: MAP_TAG,
		searchBoxTag: SEARCH_BOX_TAG,
        map: null,
		overlay: null,
		mapInited: false,
		spots: [],
		locale: null,
		markers: [],
		selectedSpot: null,
		searchBox: null,
		placesFilter: null,
		searchRadius: INITIAL_SEARCH_RADIUS,
		diffSpots: {},
		mClusterer: null,
		blockRefresh: false,
		fullscreen: false,
		q: {
			lat: 0,
			lng: 0
		}
    }),
    async created () {
        let self = this;
		self.placesFilter = PlacesFilter.getFilterFor(PlacesModes.geocode);
		this.fullscreen = SizeListener.isPhone();
		SizeListener.set('Home.fullscreen', () => {
			self.fullscreen = SizeListener.isPhone();
		});
		Ajax.setHandlers({
			errorHandler: (msg, { duration = null } = {}) => {
				self.$notify({
					title: 'Warning',
					message: msg,
					type: 'warning',
					duration
				});
			}
		});
		let q = this.$route.query;
		if (q.lat && q.lng) {
			this.q.lat = parseFloat(q.lat);
			this.q.lng = parseFloat(q.lng);
			console.log(this.q);
		}
        try {
			let locale = this.$store.getters.LOCALE;
			Logger.info('initial locale in store', locale);
			if (locale === null) {
				locale = Helpers.getLanguage();
				this.$store.commit('SET_LOCALE', locale);
				Logger.info('Store local was not found. Use ', locale, ' and commit');
			}
			this.$i18n.locale = locale;
			this.locale = locale;
			await Promise.all([
				Helpers.loadScriptAndWait(GOOGLE_MARKER_CLUSTERER),
				Helpers.loadScript(GOOGLE_MAPS_SCRIPT_URL, { onload: self.initMap })
			]);
			await this.awaitForMapInitialization();
			self.mClusterer = new MarkerClusterer(self.map, self.markers,
				{ imagePath: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m', maxZoom: 10 });
			await this.refreshSpots();
        } catch (err) {
            Logger.error(err);
			this.$router.push({ name: 'maintenance' });
        }
    },
    methods: {
		onceBlockRefresh: function () {
			this.blockRefresh = true;
		},
		ifBlockRefresh: function () {
			if (this.blockRefresh) {
				this.blockRefresh = false;
				return true;
			}
			return false;
		},
		getMarkerListener: function (spot) {
			let self = this;
			return function () {				
				self.spotSelectedHandler(spot);				
				self.$refs.spotList.setSelectedSpot(spot.ouid);
			}
		},
		fitBounds: function () {
			if (this.markers.length == 0) {
				return;
			}
			let bounds = new google.maps.LatLngBounds();
			for (let marker of this.markers) {
				bounds.extend(marker.getPosition());				
			}
			this.map.panToBounds(bounds);
			this.map.fitBounds(bounds);
		},
		refreshSpots: async function ({ position = {}, pan = true, hideProfiles = true } = {}) {
			let [err, data] = await this.getSpots(position);
			this.spots = data.body.spots;
			this.buildMarkers();
			if (pan) {
				this.fitBounds();
			}
			if (hideProfiles) {
				this.$refs.profileList.hide();
			}
		},
		awaitForMapInitialization: async function () {
			let self = this;
			return new Promise((resolve) => {
				let interval = setInterval(() => {
					if (self.mapInited) {
						clearInterval(interval);
						interval = null;
						resolve(true);
					}
				}, 100);
			});
		},
		getSpots: async function ({ latitude = 0, longitude = 0 } = {}) {
			this.$refs.spotList.setLoading(true);
			let self = this;
			const currentZoom = this.map.getZoom();
			let position;
			if (latitude == 0 && longitude == 0) {
				let currentCenter = this.map.getCenter();
				position = {
					latitude:  currentCenter.lat(),
					longitude: currentCenter.lng(),
				};
			} else {
				position = arguments[0];
			}
			let cleanLoc = new google.maps.LatLng(position.latitude, position.longitude, false);
			let resp = Ajax.getSpotsAround({
				position: {
					latitude:  cleanLoc.lat(),
					longitude: cleanLoc.lng()
				},
				radius: self.searchRadius,
				zoom:   currentZoom
			});
			this.$refs.spotList.setLoading(false);
			return resp;
		},
		moveMapTo: function (position) {
			let point = new google.maps.LatLng({
				lat: position.latitude,
				lng: position.longitude
			}); 
			if (SizeListener.isPhone()) {
				this.map.panTo(point);
			} else {
				let projection = this.overlay.getProjection(),
					pixelpoint = projection.fromLatLngToDivPixel(point);
				pixelpoint.x -= 415;
				let newpoint = projection.fromDivPixelToLatLng(pixelpoint);
				this.map.panTo(newpoint);
			}
		},
		destroyMarkers: function () {
			for (let marker of this.markers) {
				marker.setMap(null);
			}
			this.markers = [];
			this.mClusterer.clearMarkers();
		},
		getNormalizedViewRadius: function () {
			//https://stackoverflow.com/a/35418348/2157639
			let bounds = this.map.getBounds(),
				center = this.map.getCenter(),
				radius;
			if (bounds && center) {
				let ne = bounds.getNorthEast();
				radius = google.maps.geometry.spherical.computeDistanceBetween(center, ne);
			} else {
				return INITIAL_SEARCH_RADIUS;
			}
			if (radius > 20_000) {
				return 20_000;
			}
			if (radius < 5_000) {
				return 5_000;
			}
			return Math.ceil(radius);
		},
		getSpotSign: function (spot) {
			if (spot.officeClass.isForeign === true) {
				return `./img/marker-mail.svg`;
			} else {
				return `./img/marker-printer.svg`;
			}
		},
		getGMSpotIcon: function (spot, sizes) {
			return {
				url: this.getSpotSign(spot),
				...sizes
			};
		},
		buildMarkers: function () {
			let self = this;
			self.destroyMarkers();
			let tmp = {
				size:   new google.maps.Size(100, 100),
				origin: new google.maps.Point(0, 0),
				anchor: new google.maps.Point(50, 50)
			};
			for (let spot of this.spots) {
				let newMarker = new google.maps.Marker({
					position: new google.maps.LatLng(spot.latitude, spot.longitude),
					map: self.map,
					scaledSize: new google.maps.Size(150, 150),
					icon: self.getGMSpotIcon(spot, tmp)
				});
				newMarker.addListener('click', self.getMarkerListener(spot));
				this.markers.push(newMarker);
				spot.marker = newMarker;
			}
			self.mClusterer.addMarkers(self.markers);
		},
		onDragEnd: async function () {
			await this.refreshSpots({ pan: false });
		},
		onZoomChanged: function () {
			this.searchRadius = this.getNormalizedViewRadius();
			this.refreshSpots({ pan: false });
		},
        initMap: async function () {
            let self = this;
            let latitude, longitude, permission, position;
			if (self.q.lat && self.q.lng) {
				latitude = self.q.lat,
				longitude = self.q.lng
			} else {
				try {
					[permission, position] = await Helpers.askForGeolocation();
				} catch (err) {
					Logger.error(err);
					this.$router.push({ name: 'maintenance' });
				}
				if (permission === true) {
					({ latitude, longitude } = position.coords);
				} else {
					({ latitude, longitude } = DEFAULT_INITIAL_COORDS);
				}
			}
			console.log(latitude);
			console.log(longitude);
            this.$nextTick(() => {
				if (self.map === null) {
					//attach map
					let mapUISettings = SizeListener.isPhone() ? MAP_UI_SETTINGS_XS : MAP_UI_SETTINGS_SM;
					self.map = new google.maps.Map(document.getElementById(self.mapTag), {
						center: {
							lat: latitude,
							lng: longitude
						},
						minZoom: 10,
						zoom: MAP_ZOOM,
						...mapUISettings
					});
					self.overlay = new google.maps.OverlayView();
					self.overlay.draw = function () {};
					self.overlay.setMap(self.map);
					google.maps.event.addListener(self.map, 'dragend', self.onDragEnd );
					google.maps.event.addListener(self.map, 'zoom_changed', self.onZoomChanged );					
					self.mapInited = true;
				}
            });
        },
		animateDrop: function (spot) {
			if (spot.marker) {
				setTimeout(() => {
					if (spot.marker.getAnimation() !== null) {
						spot.marker.setAnimation(null);
					}
					spot.marker.setAnimation(google.maps.Animation.DROP);
				}, 500);
			}
		},
		spotSelectedHandler: async function (spot) {
			this.$refs.profileList.show();			
			if (this.selectedSpot && spot.ouid === this.selectedSpot.ouid) {
				return;
			}
			this.selectedSpot = null;
			this.moveMapTo({
				latitude:  spot.latitude,
				longitude: spot.longitude,
			});
			this.animateDrop(spot);
			let [[err, tmp]] = await Promise.all([Ajax.getSpot(spot.ouid), Helpers.notLessThan(1000)]);
			Logger.info(spot);
			this.selectedSpot = tmp.body;	
			console.log(spot);
			this.$router.push({ name: 'spot', params: { spot: spot.label } });
		},
		onPrintCall: function () {
			this.$refs.selectFileDialog.show();
		},
		uploadFilesConfirmation: async function (filesList, { email, comment }) {
			let self = this;
			Logger.info('Confirm file print for spot ', this.selectedSpot.ouid, ' aka ', this.selectedSpot.name, ' from ', email, ' with comment ', comment);
			let [err, data] = await Ajax.createJob({
				emailFrom: email, 
				emailTo:   self.selectedSpot.email, 
				text:      comment, 
				amount:    1, 
				name:      'Print from web'
			});
			if (err) {
				return [err, null];
			}
			let { juid, accessCode } = data.body;
			let p = [];
			let pcs = Array(filesList.length).fill(0);
			let tp = 0;
			let cb = function (i) {
				return function (pc) {
					pcs[i] = pc;
					self.$refs.selectFileDialog.setPercentage(Math.round(pcs.reduce((a, b) => a + b, 0) / filesList.length));
				}
			};
			for (let i = 0; i < filesList.length; i += 1) {
				p.push(Ajax.uploadFile(juid, i, filesList[i].raw, cb(i)));
			}
			await Promise.all(p);
			return [null, accessCode];
		},
		jobDone: function () {
			this.$refs.profileList.hide();
		},
		searchQuery: async function (address, cb) {
			let [err, list] = await Ajax.getPlacesAutocompletion(address);
			cb(this.placesFilter(list));
		},
		selectInput: function ({ link, value }) {
			let coords = link.split(':').map(item => parseFloat(item));
			this.moveMapTo({ latitude: coords[0], longitude: coords[1] });
			this.refreshSpots({ position: { latitude: coords[0], longitude: coords[1] } });
		},
		onLanguageChanged: function (locale) {
			this.$store.commit('SET_LOCALE', locale);
			Logger.info('Store local was changed to', locale);
			this.locale = locale;
			this.$i18n.locale = locale;
		}
    },
    components: {
        TheMap,
		SpotList,
		ProfilesList,
		UploadFile
    }
}
</script>
<style lang="scss" scoped>
.home {
    padding: 0;
    margin: 0;
}
</style>
